import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, tableDividerColumnWithAgg, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { simpleFitCell } from './FitnessTrackerListe';
import { useLocation } from 'react-router';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import routes from 'routes';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import { formatPercent, formatPercentWithIcon } from 'app/utils';
import { MazHistoryChart } from './FitnessTrackerMazHistory';

export const FitnessTrackerMaz = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [filteredAgtIds, setFilteredAgtIds] = useState<number[]>([]);

	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);
	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		/// Meine Allianz
		cols.push({
			label: 'mAZ Bestandskunden',
			path: `mazBestandDurchdringungsquote.ze`,
			sortBy: `mazBestandDurchdringungsquote.ze`,
			cellModifier: 'border-left-blue bg-blue',
			labelModifier: 'border-left-blue bg-blue',
			render(row) {
				return simpleFitCell(row.data.mazBestandDurchdringungsquote);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'mAZ Neugeschäft',
			path: `mazNeuDurchdringungsquote.ze`,
			sortBy: `mazNeuDurchdringungsquote.ze`,
			cellModifier: 'bg-blue',
			labelModifier: 'bg-blue',
			render(row) {
				return simpleFitCell(row.data.mazNeuDurchdringungsquote);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		cols.push(tableDividerColumnWithAgg);
		// VU Inbound KVM
		cols.push({
			label: (
				<>
					<span className="no-wrap">VU Inbound KVM ✚</span>
				</>
			),
			path: `kvmMonthlyAverage`,
			sortBy: `kvmMonthlyAverage`,
			cellModifier: 'bg-blue',
			labelModifier: 'bg-blue',
			render(row) {
				if (!row.data.kvmMonthlyAverage) return <></>;
				return <>{formatPercent(row.data.kvmMonthlyAverage, 1)}</>;
			},
			agg: {
				aggType: AggregrationType.sum,
				format: (v) => formatPercent(v, 1),
			},
		});
		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'maz', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			// Store all agency IDs for aggregated chart view
			if (items.length > 0) {
				const agtIds = items.map((item) => item.agt.agtId);
				setFilteredAgtIds(agtIds);
			} else {
				setFilteredAgtIds([]);
			}
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'maz');
		history.pushWithQuery(p);
	};

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
				) : (
					<>
						{!selectedAgtId && filteredAgtIds.length > 0 && (
							<div className="pad-1rem pad-bottom-2rem  is-sticky">
								<MazHistoryChart agtIds={filteredAgtIds} />
							</div>
						)}
						{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="maz" onClose={onClose} />}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});
